import type { ColumnDef } from '../projects/projectsColumns';
import { testStatuses, testTypes, priorities, teamMembers } from '../../constants/statusOptions';
import type { GridColumn } from '../../components/tables/EditableGrid';

/**
 * Work-order status chip config — shared by the WO header chip/menu and the
 * side-panel status dots. `label` doubles as the menu text; `dot` is the
 * SidePanel status-dot colour (MUI palette dots, not theme.schedule tokens).
 */
export const WO_STATUS_CONFIG: Record<string, {
  color: 'default' | 'warning' | 'info' | 'success' | 'error';
  label: string;
  dot: string;
}> = {
  Scheduled: { color: 'default', label: 'Scheduled', dot: '#9e9e9e' },
  'In Progress': { color: 'info', label: 'In Progress', dot: '#2196f3' },
  'On Hold': { color: 'warning', label: 'On Hold', dot: '#ff9800' },
  Completed: { color: 'success', label: 'Completed', dot: '#4caf50' },
  Cancelled: { color: 'error', label: 'Cancelled', dot: '#f44336' },
};

export const workOrderDetailColumns: ColumnDef[] = [
  { field: 'LocationName', headerName: 'Location', sortable: true, width: 130 },
  { field: 'SampleId', headerName: 'Sample Reference', sortable: true, width: 120 },
  { field: 'DepthTop', headerName: 'Depth Top', sortable: true, width: 90 },
  { field: 'DepthBase', headerName: 'Depth Base', sortable: true, width: 90 },
  { field: 'SampleType', headerName: 'Type', sortable: true, width: 100 },
  { field: 'SpecimenRef', headerName: 'Specimen Ref', sortable: true, width: 100 },
  { field: 'SpecimenDepth', headerName: 'Specimen Depth', sortable: true, width: 110 },
  { field: 'TestName', headerName: 'Test Name', sortable: true, flex: 1 },
  { field: 'TestStatus', headerName: 'Test Status', sortable: true, width: 120 },
  { field: 'Instructions', headerName: 'Instructions', sortable: false, flex: 1 },
];

/**
 * BUG-2026-0159: leading columns kept frozen (sticky-left) in the WO samples
 * grid so Location/Sample/Depths stay visible while scrolling to the test
 * columns. Order matches the head of workOrderDetailGridColumns; the freeze
 * count is derived from whichever of these the user has left visible.
 */
export const WO_FROZEN_FIELDS: readonly string[] = [
  '_action', 'LocationName', 'SampleId', 'DepthTop', 'DepthBase',
];

export const workOrderDetailGridColumns: GridColumn[] = [
  // 3 icons since the history action (mig 110): workflow, remove, history.
  { field: '_action', headerName: '', type: 'actions', width: 100 },
  { field: 'LocationName', headerName: 'Location', type: 'text', width: 130, readonly: true },
  { field: 'SampleId', headerName: 'Sample Reference', type: 'text', width: 120, readonly: true },
  { field: 'DepthTop', headerName: 'Depth Top', type: 'number', width: 90, readonly: true },
  { field: 'DepthBase', headerName: 'Depth Base', type: 'number', width: 90, readonly: true },
  { field: 'SampleType', headerName: 'Type', type: 'text', width: 100, readonly: true },
  { field: 'SpecimenRef', headerName: 'Specimen Ref', type: 'text', width: 100 },
  { field: 'SpecimenDepth', headerName: 'Specimen Depth', type: 'number', width: 110 },
  // Read-only: TestName is set at sample-add time via AddSamplesModal
  // (testKeys[]). Changing it after the fact would orphan downstream test
  // data (DirectShearGeneral/Detail/Reading keyed by WorkOrderDetailKey).
  { field: 'TestName', headerName: 'Test Name', type: 'select', options: testTypes, flex: 1, readonly: true },
  {
    field: 'TestStatus',
    headerName: 'Test Status',
    type: 'select',
    options: testStatuses,
    width: 130,
  },
  {
    field: 'Priority',
    headerName: 'Priority',
    type: 'select',
    options: priorities,
    width: 110,
  },
  { field: 'StartDate', headerName: 'Start Date', type: 'date', width: 140 },
  { field: 'EndDate', headerName: 'End Date', type: 'date', width: 140 },
  { field: 'CheckedBy', headerName: 'Tested By', type: 'select', options: teamMembers, width: 140 },
  { field: 'TestDate', headerName: 'Test Date', type: 'date', width: 110 },
  { field: 'ReviewedBy', headerName: 'Reviewed By', type: 'select', options: teamMembers, width: 140 },
  { field: 'ReviewDate', headerName: 'Review Date', type: 'date', width: 110 },
  { field: 'ApprovedBy', headerName: 'Approved By', type: 'select', options: teamMembers, width: 140 },
  { field: 'ApprovalDate', headerName: 'Approval Date', type: 'date', width: 110 },
  { field: 'Instructions', headerName: 'Instructions', type: 'text', flex: 1 },
];

export const sampleSelectColumns: ColumnDef[] = [
  { field: 'SampleId', headerName: 'Sample Reference', sortable: true, width: 120 },
  { field: 'LocationId', headerName: 'Location', sortable: true, width: 130 },
  { field: 'DepthTop', headerName: 'Depth Top', sortable: true, width: 90 },
  { field: 'DepthBase', headerName: 'Depth Base', sortable: true, width: 90 },
  { field: 'SampleType', headerName: 'Type', sortable: true, width: 100 },
  { field: 'Status', headerName: 'Status', sortable: true, width: 100 },
];
